import type { SyncScheduleDto } from '../../utils/syncSchedule';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Run statuses that mean the sync finished and pushed products (an `error` run did not). */
const SUCCESS_STATUSES = new Set(['success', 'partial', 'completed']);

const INTERVAL_MS: Record<'hourly' | 'daily' | 'weekly', number> = {
  hourly: 60 * 60 * 1000,
  daily: DAY_MS,
  weekly: 7 * DAY_MS,
};

/** Newest-first run list in, newest successful run out (null when none has succeeded). */
export function pickLatestSuccessfulSyncRun<T extends { status?: string }>(runs: T[]): T | null {
  return runs.find((run) => SUCCESS_STATUSES.has(String(run.status || ''))) || null;
}

/**
 * How old the last successful sync may get before the store should be told.
 * Two missed intervals (never under a day, so an hourly schedule is not nagging)
 * when auto-sync is on; a week for manual-only integrations.
 */
export function staleSyncThresholdMs(schedule: SyncScheduleDto | null | undefined): number {
  if (schedule?.autoSync && (schedule.interval === 'hourly' || schedule.interval === 'daily' || schedule.interval === 'weekly')) {
    return Math.max(DAY_MS, 2 * INTERVAL_MS[schedule.interval]);
  }
  return 7 * DAY_MS;
}

/** Whole days since the last successful sync when that is past the threshold, else null. */
export function staleSyncDays(
  lastSuccessAt: string | null | undefined,
  schedule: SyncScheduleDto | null | undefined,
  now: number = Date.now()
): number | null {
  if (!lastSuccessAt) return null;
  const at = new Date(lastSuccessAt).getTime();
  if (Number.isNaN(at)) return null;
  const age = now - at;
  if (age <= staleSyncThresholdMs(schedule)) return null;
  return Math.floor(age / DAY_MS);
}
